import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { IconClose } from './icons.jsx';
import { useConfirm } from './feedback.jsx';
import { t, fmtDate as i18nFmtDate, enumLabel } from '../i18n/index.jsx';

export function Modal({ title, onClose, children, footer, dirty = false }) {
  const confirm = useConfirm();
  const downOnBackdrop = useRef(false);
  const closing = useRef(false);

  // Centralised close that optionally guards against discarding unsaved edits.
  const requestClose = useCallback(async () => {
    if (closing.current) return;
    if (dirty) {
      closing.current = true;
      const ok = await confirm({
        title: t('Discard changes?'),
        message: t('You have unsaved changes. Close this form without saving?'),
        confirmLabel: t('Discard'), cancelLabel: t('Keep editing'), danger: true,
      });
      closing.current = false;
      if (!ok) return;
    }
    onClose();
  }, [dirty, confirm, onClose]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape' && !closing.current) requestClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [requestClose]);

  // Only dismiss on a backdrop click when BOTH the press and release land on
  // the backdrop. A drag/misclick that starts inside a field no longer closes
  // the modal (and loses unsaved input).
  const onBackdropDown = (e) => { downOnBackdrop.current = e.target === e.currentTarget; };
  const onBackdropUp = (e) => {
    if (downOnBackdrop.current && e.target === e.currentTarget) requestClose();
    downOnBackdrop.current = false;
  };

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return createPortal(
    <div className="modal-backdrop" onMouseDown={onBackdropDown} onMouseUp={onBackdropUp}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="modal-head">
          <h3>{title}</h3>
          <button className="icon-btn" onClick={requestClose} aria-label="Close"><IconClose width={18} height={18} /></button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

// Previous / Page N of M / Next control driven by a `meta` object from the API.
export function Pagination({ meta, onPage }) {
  if (!meta || meta.total_pages <= 1) return null;
  const { page, total_pages: totalPages, total } = meta;
  return (
    <div className="pagination">
      <button className="btn ghost sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>{t('Previous')}</button>
      <span className="muted sm">{t('Page {a} of {b} · {n} total', { a: page, b: totalPages, n: total })}</span>
      <button className="btn ghost sm" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>{t('Next')}</button>
    </div>
  );
}

export function Skeleton({ width = '100%', height = 14, radius = 6, style }) {
  return <span className="skeleton" style={{ width, height, borderRadius: radius, ...style }} />;
}

// Table-shaped placeholder shown while a list page's rows load.
export function SkeletonTable({ rows = 6, cols = 4 }) {
  return (
    <div className="table-wrap">
      <table className="table">
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r}>
              {Array.from({ length: cols }).map((_, c) => (
                <td key={c}><Skeleton width={c === 0 ? '65%' : '40%'} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Field({ label, error, children }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}

export function Badge({ tone = 'gray', children }) {
  return <span className={`badge ${tone}`}>{children}</span>;
}

export function Empty({ children }) {
  return (
    <div className="empty">
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{opacity: 0.45}}>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M9 9h.01M15 9h.01M8 15h8" />
      </svg>
      <div>{children}</div>
    </div>
  );
}

export function Spinner() {
  return <div className="full-center"><div className="spinner" /></div>;
}

// Maps domain enums -> badge tones.
export const STATUS_TONE = {
  // projects
  lead: 'blue', active: 'green', on_hold: 'amber', completed: 'gray', cancelled: 'red',
  // contracts
  draft: 'gray', sent: 'blue', signed: 'green', expired: 'red',
  // tasks
  todo: 'gray', in_progress: 'blue', blocked: 'red', review: 'amber', done: 'green',
  // funds
  scouting: 'gray', open: 'blue', reporting: 'amber', closed: 'gray',
  // fund_clients (lead/active reuse the project tones above)
  preparing: 'amber', submitted: 'blue', approved: 'green', rejected: 'red',
  // documents
  to_fill: 'amber', filled: 'blue',
  // sal milestones
  planned: 'gray', paid: 'green',
  // invoices (paid reuses the sal tone above)
  partially_paid: 'amber', overdue: 'red',
};

export const PRIORITY_TONE = { low: 'gray', medium: 'blue', high: 'amber', urgent: 'red' };

// Locale-aware; delegates to the i18n module so dates follow the UI language.
export const fmtDate = i18nFmtDate;

// Human, translated label for a status/priority/type enum value.
export const label = enumLabel;

export function daysUntil(d) {
  if (!d) return null;
  const ms = new Date(d + 'T00:00:00') - new Date(new Date().toDateString());
  return Math.round(ms / 86400000);
}
